import { tryCatch } from "@/utils/try-catch";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import { useState, useContext, useEffect } from "react";
import { useForm, SubmitHandler, UseFormRegister } from "react-hook-form";
import { AuthFlowType, FLOW_CONFIGS, FlowConfig, FlowType } from "@/config/VerifyOtpFlowConfig";

import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";

import { FormError } from "@/models/continente-credentials/common/FormError"

import { divideEmail } from "@/utils/EmailDivider";

import { Button } from "@/components/ui/button";
import CredentialsForm from "@/components/Form/Form";
import OtpInput from "@/components/otp-input/OtpInput";
import CredentialsHeader from "@/components/header/Header";
import NeedHelpButton from "@/components/need-help-button/need-help-button";

import { UserContext } from "@/contexts/continente-credentials/UserContext";
import { DigitalAssetContext } from "@/contexts/continente-credentials/DigitalAssetContext";
import { cn } from "@/utils/utils";
import { EmailAssociationOtpValidation, PhoneNumberAssociationOtpValidation } from "@/models/shared/OtpValidationRequest";
import { otpValidationService } from "@/services/shared/OtpValidationService";
import { analyticsService } from "@/services/AnalyticsService";

const ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION = "";

interface ValidatePasswordPageProps {
  flowConfig: FlowConfig;
}

type Form = (PhoneNumberAssociationOtpValidation | EmailAssociationOtpValidation) & FormError;

export default function SharedVerifyOtpPage({ flowConfig }: ValidatePasswordPageProps) {
  const history = useHistory();
  const location = useLocation<any>();
  const { t } = useTranslation();
  const userContext = useContext(UserContext);

  const { handleResponse } = useApiResponseHandler();

  const digitalAssetContext = useContext(DigitalAssetContext);

  const [loading, setLoading] = useState(false);
  const [isAdminBlocked, setAdminBlocked] = useState(false);

  const userData = location.state?.properties || {};
  const userIdentifier = userData.identifier || userData[flowConfig.identifierField] || "";
  const nextStep = location.state?.nextStep || "";

  const requiredAssociation =
    ((flowConfig.flow == AuthFlowType.EMAIL_ASSOCIATION) && digitalAssetContext.digitalAsset.requiredEmailAssociation) ||
    ((flowConfig.flow == AuthFlowType.PHONE_ASSOCIATION) && digitalAssetContext.digitalAsset.requiredPhoneNumberAssociation);

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const handleContactFormLinkClick = () => {
    history.push("/continente-credentials/faq" + ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION);
  };

  const handleOTPChange = (value: string) => {
    clearErrors();
    setValue("code", value.trim());
  };

  useEffect(() => {
    const otpValidator = (register: UseFormRegister<Form>) =>
      register("code", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^[0-9]*$/,
          message: t("mfa_validation.code_must_be_numeric"),
        },
      });
    otpValidator(register);
  }, [register, t]);

  const onSubmit: SubmitHandler<PhoneNumberAssociationOtpValidation | EmailAssociationOtpValidation> = async (data) => {
    setLoading(true);
    
    const channel = flowConfig.identifierField === 'email' ? 'email' : 'sms';
    analyticsService.trackOtpSubmitted(flowConfig.flow, channel);

    const { data: response, error } = await tryCatch(
      otpValidationService.otpValidation(
        {
          code: data.code,
          ...(flowConfig.identifierField === 'email' ?
            { email: userIdentifier } :
            { phoneNumber: userIdentifier }),
        },
        nextStep
      ));

    if (response?.kind === 'error') {
      const errorCode = response.code || 'invalid_otp';
      analyticsService.trackOtpFailure(flowConfig.flow, errorCode);
    }

    handleResponse(response, {
      setError,
      setAdminBlocked,
      onSuccess: (response) => {
        analyticsService.trackOtpSuccess(flowConfig.flow);

        response.value.previousStep = "validate-otp";
        history.push(response.value.nextStep.substring(4), response.value);
      }
    }, error);

    setLoading(false);
  };

  const skipAssociation = async () => {
    setLoading(true);

    const { data: response, error } = await tryCatch(otpValidationService.otpValidation(
      {
        skipAssociation: true,
      },
      nextStep
    ));

    handleResponse(response, {
      setError,
      onSuccess: (response) => {
        userContext.updateUser({
          ...userContext.user,
          email: "",
        });
        response.value.previousStep =
          "validate-otp";
        history.push(response.value.nextStep.substring(4), response.value);
      },
      setAdminBlocked,
    }, error);

    setLoading(false);
  };

  return (
    <CredentialsForm
      id={flowConfig.form}
      autoComplete="on"
      form={formState}
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <CredentialsHeader
        title={t(flowConfig.labels.headerTitle)}
        subtitle={cn(
          t(flowConfig.labels.headerSubtitle ?? ""),
          (flowConfig.identifierField == "email" ? divideEmail(userIdentifier) : userIdentifier))
        }
        showLoading={loading}
        previousStepBackXTimes={flowConfig.previousStepBackXTimes}
      />
      <OtpInput
        isError={!!(errors.code || errors.blocked || errors.submitError)}
        errorMsg={
          errors.code?.message ||
          errors.blocked?.message ||
          errors.submitError?.message ||
          ""
        }
        onChange={handleOTPChange}
        onResendClick={setLoading}
        isMfa={flowConfig.resendStrategy === "mfa"}
        isEmail={flowConfig.otpChannel === "email"}
        resendStrategy={flowConfig.resendStrategy}
        required
        onInvalid={(e) => {
          e.preventDefault()
          if (e.target.validity.valueMissing) {
            setError("code", { message: t("common.required_field") });
          }
        }}
      />
      {errors.blocked && isAdminBlocked && (
        <div className="h-10 text-7xl font-bold text-center mb-30">
          <span
            onClick={handleContactFormLinkClick}
            className="text-black hover:underline cursor-pointer font-bold"
          >
            {" "}
            {t("common.contact_form")}{" "}
          </span>
        </div>
      )}
      <div className="flex flex-col w-full items-center gap-3">
        <Button
          disabled={loading}>
          {t(flowConfig.labels.nextButton)}
        </Button>
        {flowConfig.type == FlowType.ASSOCIATION && !requiredAssociation && (
          <Button
            type="button"
            variant="secondary"
            onClick={skipAssociation}
            disabled={loading}>
            {t(flowConfig.labels.skipButton ?? "")}
          </Button>
        )}
        <NeedHelpButton />
      </div>
    </CredentialsForm>
  );
}

export const createValidatePasswordPage = (flowConfig: AuthFlowType) => {
  const config = FLOW_CONFIGS[flowConfig];
  return () => <SharedVerifyOtpPage flowConfig={config} />;
};

export const EmailMfaValidation = createValidatePasswordPage(AuthFlowType.EMAIL_MFA);
export const PhoneNumberMfaValidation = createValidatePasswordPage(AuthFlowType.PHONE_MFA);
export const PasswordRecoverPhoneNumberValidateOtp = createValidatePasswordPage(AuthFlowType.RECOVER_PASSWORD_PHONE);
export const PasswordRecoverPhoneNumberValidateOtpEmail = createValidatePasswordPage(AuthFlowType.RECOVER_PASSWORD_PHONE_OTP_TO_EMAIL);
export const PasswordRecoverEmailValidateOtp = createValidatePasswordPage(AuthFlowType.RECOVER_PASSWORD_EMAIL);
export const SCPasswordRecoverPhoneNumberValidateOtp = createValidatePasswordPage(AuthFlowType.SC_RECOVER_PASSWORD_PHONE);
export const SCPasswordRecoverEmailValidateOtp = createValidatePasswordPage(AuthFlowType.SC_RECOVER_PASSWORD_EMAIL);

export const EmailVerifyOtp = createValidatePasswordPage(AuthFlowType.EMAIL);
export const PhoneNumberVerifyOtp = createValidatePasswordPage(AuthFlowType.PHONE);
export const EmailAssociationVerifyOtp = createValidatePasswordPage(AuthFlowType.EMAIL_ASSOCIATION);
export const PhoneNumberAssociationVerifyOtp = createValidatePasswordPage(AuthFlowType.PHONE_ASSOCIATION);
export const PhoneNumberFreshStartVerifyOtp = createValidatePasswordPage(AuthFlowType.PHONE_FRESH_START);
